import { apiClient } from './client';
import type { ClientsPageMeta } from './clients';

/**
 * Администратор — /admin-users (см. докблок AdminUserController в Laravel,
 * 1:1 старые Orchid UserListScreen/UserEditScreen).
 */
export interface AdminUserRow {
  id: number;
  name: string | null;
  lastName: string | null;
  firstName: string | null;
  surname: string | null;
  email: string | null;
  status: string | null;
  roles: string[];
}

export interface AdminUserDetail {
  id: number;
  name: string | null;
  lastName: string | null;
  firstName: string | null;
  surname: string | null;
  email: string | null;
  status: string | null;
  /** Единый логин на сервере авторизации. */
  login: string | null;
  /** Сервер MT1 для синхронизации; null — не синхронизировать. */
  mt1Server: string | null;
  roleIds: number[];
  permissions: string[];
}

export interface AdminUserOptions {
  roles: { value: string; label: string }[];
  statuses: { value: string; label: string }[];
  mt1Servers: { value: string; label: string }[];
}

export interface FetchAdminUsersParams {
  search?: string;
  status?: string;
  roleId?: string;
  sort?: string;
  page?: number;
}

export interface AdminUsersPage {
  items: AdminUserRow[];
  meta: ClientsPageMeta;
}

export async function fetchAdminUsers(params: FetchAdminUsersParams = {}): Promise<AdminUsersPage> {
  const { data } = await apiClient.get<AdminUsersPage>('/admin-users', { params });
  return data;
}

export async function fetchAdminUserOptions(): Promise<AdminUserOptions> {
  const { data } = await apiClient.get<AdminUserOptions>('/admin-users/options');
  return data;
}

export async function fetchAdminUser(id: number): Promise<AdminUserDetail> {
  const { data } = await apiClient.get<AdminUserDetail>(`/admin-users/${id}`);
  return data;
}

export interface SaveAdminUserPayload {
  name: string;
  lastName?: string | null;
  firstName?: string | null;
  surname?: string | null;
  email: string;
  status: string;
  login?: string | null;
  /** Пустой — пароль не меняется. */
  password?: string | null;
  mt1Server?: string | null;
  roleIds: number[];
  permissions: string[];
}

/**
 * Создание регистрирует логин на SSO и привязывает роли к обеим админкам —
 * старой и новой (см. AdminServers в Laravel), с хуками обратно в админку.
 * Цепочка занимает до полуминуты, поэтому таймаут как у createClient():
 * оборвать ожидание раньше значит показать ошибку по уже созданной записи.
 */
export async function createAdminUser(payload: SaveAdminUserPayload): Promise<AdminUserDetail> {
  const { data } = await apiClient.post<AdminUserDetail>('/admin-users', payload, {
    timeout: 95_000,
  });
  return data;
}

/** Правка тоже пересинхронизирует привязки на SSO — таймаут как у createAdminUser(). */
export async function updateAdminUser(
  id: number,
  payload: SaveAdminUserPayload,
): Promise<AdminUserDetail> {
  const { data } = await apiClient.put<AdminUserDetail>(`/admin-users/${id}`, payload, {
    timeout: 95_000,
  });
  return data;
}

export async function deleteAdminUser(id: number): Promise<void> {
  await apiClient.delete(`/admin-users/${id}`);
}
